import { brandingFor, questionsForSelection, termsFor } from '@dwa/shared';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { QuestionCard } from '../components/QuestionCard';
import { ScaleLegend } from '../components/ScaleLegend';
import { Button, ErrorBanner, Spinner } from '../components/ui';
import { useFramework } from '../hooks/useFramework';
import { useAssessmentStore } from '../state/assessmentStore';

export function Review() {
  const navigate = useNavigate();
  const focus = useAssessmentStore((s) => s.focus);
  const fw = useFramework(focus ?? undefined);
  const pillars = useAssessmentStore((s) => s.pillars);
  const answers = useAssessmentStore((s) => s.answers);
  const setAnswer = useAssessmentStore((s) => s.setAnswer);
  const [step, setStep] = useState(0);
  // Set once the user tries to finish with gaps; reveals the unanswered highlights.
  const [attempted, setAttempted] = useState(false);

  // No WAR chosen (e.g. deep-link straight here) -> back to the chooser.
  if (!focus) return <Navigate to="/" replace />;
  if (fw.isPending) return <Spinner label="Loading the framework…" />;
  if (fw.isError || !fw.data) return <ErrorBanner message="Couldn’t load the framework. Please retry." />;
  const framework = fw.data;
  const { referenceLabel } = brandingFor(framework);
  const terms = termsFor(framework);

  const activePillars = framework.pillars.filter((p) => pillars.includes(p.id));
  if (activePillars.length === 0) return <Navigate to={`/${focus}`} replace />;

  const idx = Math.min(step, activePillars.length - 1);
  const pillar = activePillars[idx]!;
  const questions = framework.questions.filter((q) => q.pillarId === pillar.id);
  const isLast = idx === activePillars.length - 1;

  const answeredIn = (pillarId: string) =>
    framework.questions.filter((q) => q.pillarId === pillarId && answers[q.id]).length;

  const total = questionsForSelection({ pillars }, framework).length;
  const answeredTotal = activePillars.reduce((n, p) => n + answeredIn(p.id), 0);
  const unansweredCount = total - answeredTotal;
  const hasGapIn = (pillarId: string) =>
    framework.questions.some((q) => q.pillarId === pillarId && !answers[q.id]);

  const goTo = (next: number) => {
    setStep(next);
    window.scrollTo({ top: 0 });
  };
  const finish = () => {
    if (unansweredCount > 0) {
      setAttempted(true);
      // Jump to the first pillar that still has a gap so the highlights are visible.
      const firstGap = activePillars.findIndex((p) => hasGapIn(p.id));
      if (firstGap >= 0 && firstGap !== idx) goTo(firstGap);
      else window.scrollTo({ top: 0 });
      return;
    }
    navigate('/get-report');
  };
  const next = () => (isLast ? finish() : goTo(idx + 1));
  const prev = () => idx > 0 && goTo(idx - 1);

  return (
    <div className="space-y-6">
      {/* stepper */}
      <div className="no-print">
        <div className="flex items-center justify-between text-sm text-climb-teal-soft">
          <span>
            Step {idx + 1} of {activePillars.length}
          </span>
          <span>
            {answeredTotal} / {total} answered
          </span>
        </div>
        <div className="mt-2 flex gap-1.5">
          {activePillars.map((p, i) => {
            const done = answeredIn(p.id) === framework.questions.filter((q) => q.pillarId === p.id).length;
            const isCurrent = i === idx;
            return (
              <button
                key={p.id}
                type="button"
                title={p.name}
                aria-label={`Go to ${p.name}`}
                onClick={() => goTo(i)}
                className={`h-1.5 flex-1 rounded-full transition ${
                  isCurrent
                    ? 'bg-climb-cyan'
                    : done
                      ? 'bg-climb-teal-soft'
                      : attempted
                        ? 'bg-climb-coral'
                        : 'bg-climb-line-dark hover:bg-climb-teal-soft/50'
                }`}
              />
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-climb-cyan">
          {pillar.category === 'Lens' ? terms.lensItemLabel : terms.coreItemLabel}
        </div>
        <h1 className="mt-1 text-2xl font-medium text-climb-paper">{pillar.name}</h1>
        <p className="mt-1 text-climb-teal-soft">{pillar.description}</p>
        <p className="mt-1 text-sm text-climb-teal-soft/70">
          {answeredIn(pillar.id)} of {questions.length} answered in this {terms.unit}
        </p>
      </div>

      {attempted && unansweredCount > 0 && (
        <div
          role="alert"
          className="no-print rounded-lg border border-climb-coral bg-climb-coral/10 px-4 py-3 text-sm text-climb-paper"
        >
          <span className="font-semibold text-climb-coral">{unansweredCount} unanswered</span>{' '}
          {unansweredCount === 1 ? 'question remains' : 'questions remain'}. Score each one (or mark it N/A) to get
          your report. Unanswered questions are outlined in coral below.
        </div>
      )}

      <ScaleLegend />

      <div className="space-y-4">
        {questions.map((q) => (
          <QuestionCard
            key={q.id}
            question={q}
            answer={answers[q.id]}
            onSave={(score, notes) => setAnswer(q.id, score, notes)}
            incomplete={attempted && !answers[q.id]}
            referenceLabel={referenceLabel}
          />
        ))}
      </div>

      <div className="no-print sticky bottom-0 -mx-6 flex items-center justify-between border-t border-climb-line-dark bg-climb-teal/95 px-6 py-3 backdrop-blur">
        <Button variant="secondary" onClick={prev} disabled={idx === 0}>
          ← Previous
        </Button>
        <Button onClick={next}>{isLast ? 'Get my report →' : 'Next pillar →'}</Button>
      </div>
    </div>
  );
}
